import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FileTextService } from '../../core/services/file-text.service';
import { ChatAttachment } from '../../core/models/chat-message';
import { AttachmentService } from '../../core/services/attachment.service';
import { BillSplitterAiService } from './bill-splitter-ai.service';
import { BillSplitterCalculatorService, ChargeKey } from './bill-splitter-calculator.service';
import { Bill, BillItem, BillSplitterMode, BillSplitterResult, ChargeAllocationStrategy, newId } from './bill-splitter.models';
import { BillSplitterService } from './bill-splitter.service';
import { BillSplitterExportService } from './bill-splitter-export.service';
import { BillPersonComponent } from './components/bill-person/bill-person.component';
import { BillStorageService } from './bill-storage.service';

@Component({
  selector: 'app-bill-splitter', standalone: true, imports: [FormsModule, CurrencyPipe, NgTemplateOutlet, BillPersonComponent],
  templateUrl: './bill-splitter.component.html', styleUrl: './bill-splitter.component.scss',
})
export class BillSplitterComponent implements OnInit {
  @Input() mode: BillSplitterMode = 'page';
  @Input() billId?: string;
  @Input() initialPeople: string[] = [];
  @Input() sourceText = '';
  @Input() sourceAttachment?: ChatAttachment;
  @Input() initialInstructions = '';
  @Output() completed = new EventEmitter<BillSplitterResult>();

  readonly store = inject(BillSplitterService);
  readonly calculator = inject(BillSplitterCalculatorService);
  private readonly ai = inject(BillSplitterAiService);
  private readonly attachments = inject(AttachmentService);
  private readonly exporter = inject(BillSplitterExportService);
  private readonly storage = inject(BillStorageService);
  private readonly route = inject(ActivatedRoute, { optional: true });
  private readonly currentId = signal('');
  readonly bill = computed(() => this.store.getBill(this.currentId()));
  readonly validation = computed(() => this.bill() ? this.calculator.validateBill(this.bill()!) : undefined);
  readonly sharedItems = computed(() => this.bill()?.items.filter((i) => this.calculator.isShared(i, this.bill()!.people)) ?? []);
  readonly personCards = computed(() => {
    const bill = this.bill();
    if (!bill) return [];
    return bill.people.map((person) => ({
      ...person, total: this.calculator.calculatePersonTotal(bill, person.id),
      adjustments: this.calculator.calculatePersonTotal(bill, person.id) - this.calculator.calculatePersonItems(bill, person.id),
      items: bill.items.filter((i) => this.calculator.isShared(i, bill.people) || i.allocations.some((a) => a.personId === person.id && a.share > 0))
        .map((item) => ({ item, shared: this.calculator.isShared(item, bill.people), total: this.calculator.calculateItemContribution(item, person.id, bill.people) })),
    }));
  });
  readonly busy = signal(false);
  readonly status = signal('');
  readonly error = signal('');
  readonly notice = signal('');
  readonly importOpen = signal(false);
  readonly activeDrop = signal<string | undefined>(undefined);
  readonly dragging = signal(false);
  readonly selectedItemId = signal<string | undefined>(undefined);
  readonly confirmation = signal<Bill | undefined>(undefined);
  readonly clearRequested = signal(false);
  readonly accept = 'image/*,' + FileTextService.ACCEPT;
  readonly charges: Array<{ key: ChargeKey; label: string }> = [
    { key: 'discount', label: 'Bill discount' }, { key: 'tax', label: 'Tax' },
    { key: 'serviceCharge', label: 'Service charge' }, { key: 'tip', label: 'Tip' },
  ];
  personName = '';
  pastedText = '';
  draft = this.emptyDraft();
  editorOpen = false;
  allocationMode: 'everyone' | 'selected' = 'everyone';
  allocationWeights: Record<string, number> = {};
  selectedPeople: string[] = [];
  private lastSourceAttachment?: ChatAttachment;

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    if (!this.billId && this.mode === 'page') this.billId = this.route?.snapshot.paramMap.get('billId') ?? undefined;
    let bill = this.billId ? this.store.getBill(this.billId) : this.store.activeBill();
    if (!bill && this.billId) {
      try {
        const stored = await this.storage.load(this.billId);
        if (stored) bill = this.store.restoreBill(stored);
      } catch {
        // The local bill remains usable when an offline database cannot be reached.
      }
    }
    if (!bill) bill = this.store.createBill(this.billId ? { id: this.billId } : {});
    this.currentId.set(bill.id);
    this.store.activate(bill.id);
    for (const name of bill.sourceProcessed ? [] : this.initialPeople) {
      if (!bill.people.some((p) => p.name === name)) this.store.applyOperation(bill.id, { type: 'add_person', name });
    }
    if (!bill.sourceProcessed && !bill.items.length && (this.sourceText.trim() || this.sourceAttachment)) {
      this.pastedText = this.sourceText;
      void this.importSource(this.sourceText, this.sourceAttachment, this.initialInstructions);
    }
  }

  clearBill(): void {
    if (this.busy() || !this.clearRequested()) return;
    this.store.clearBill(this.currentId());
    this.closeEditor();
    this.personName = '';
    this.pastedText = '';
    this.sourceText = '';
    this.sourceAttachment = undefined;
    this.initialInstructions = '';
    this.initialPeople = [];
    this.lastSourceAttachment = undefined;
    this.draft = this.emptyDraft();
    this.selectedPeople = [];
    this.allocationWeights = {};
    this.allocationMode = 'everyone';
    this.confirmation.set(undefined);
    this.error.set('');
    this.status.set('');
    this.dragEnd();
    this.importOpen.set(false);
    this.clearRequested.set(false);
    this.notice.set('Bill cleared. You can import a new receipt or start manually.');
  }

  exportSplit(): void {
    const bill = this.bill();
    if (!bill || this.busy() || !bill.people.length) return;
    this.exporter.download(bill);
    this.notice.set('Excel workbook downloaded.');
  }

  async saveBill(): Promise<void> {
    const bill = this.bill();
    if (!bill || this.busy() || !bill.items.length) return;
    this.busy.set(true); this.status.set('Saving bill…'); this.error.set('');
    try {
      const saved = await this.storage.save(bill, this.lastSourceAttachment);
      this.store.restoreBill(saved);
      this.notice.set('Bill and original source file saved.');
    } catch (e) { this.error.set(this.errorMessage(e)); }
    finally { this.busy.set(false); this.status.set(''); }
  }

  addPerson(): void {
    if (!this.personName.trim() || this.busy()) return;
    this.store.applyOperation(this.currentId(), { type: 'add_person', name: this.personName });
    this.personName = '';
  }
  renamePerson(personId: string, name: string): void {
    this.store.applyOperation(this.currentId(), { type: 'rename_person', personId, name });
  }
  removePerson(personId: string): void {
    this.store.applyOperation(this.currentId(), { type: 'remove_person', personId });
    this.closeEditor();
  }
  newItem(): void {
    this.selectedItemId.set(undefined);
    this.draft = this.emptyDraft();
    this.allocationMode = 'everyone';
    this.selectedPeople = [];
    this.allocationWeights = {};
    this.error.set('');
    this.editorOpen = true;
  }
  edit(item: BillItem): void {
    this.selectedItemId.set(item.id);
    this.draft = { name: item.name, quantity: item.quantity, total: item.total / 100,
      unitPrice: item.unitPrice == null ? null : item.unitPrice / 100,
      unitDiscount: (item.unitDiscount ?? 0) / 100, discount: (item.discount ?? 0) / 100 };
    this.allocationMode = this.calculator.isShared(item, this.bill()!.people) ? 'everyone' : 'selected';
    this.selectedPeople = item.allocations.map((a) => a.personId);
    this.allocationWeights = Object.fromEntries(item.allocations.map((a) => [a.personId, a.share * 100]));
    this.editorOpen = true;
    this.error.set('');
  }
  closeEditor(): void { this.editorOpen = false; this.selectedItemId.set(undefined); }
  recalculateDraft(): void {
    if (this.draft.unitPrice != null && Number.isFinite(this.draft.quantity) && this.draft.quantity > 0) {
      this.draft.total = this.calculator.lineTotal(this.draft.quantity, this.calculator.toMinor(this.draft.unitPrice)) / 100;
    }
  }
  toggleSelected(personId: string): void {
    this.selectedPeople = this.selectedPeople.includes(personId) ? this.selectedPeople.filter((p) => p !== personId) : [...this.selectedPeople, personId];
    this.equalWeights();
  }
  equalWeights(): void {
    this.allocationWeights = Object.fromEntries(this.selectedPeople.map((id) => [id, 1]));
  }
  saveItem(): void {
    try {
      if (!this.draft.name.trim() || !Number.isFinite(this.draft.quantity) || this.draft.quantity <= 0) throw new Error('Enter an item name and a quantity greater than zero.');
      if (this.draft.total == null || !Number.isFinite(this.draft.total)) throw new Error('Enter a valid line price or credit.');
      if ([this.draft.unitDiscount, this.draft.discount].some((v) => !Number.isFinite(v) || v < 0)) throw new Error('Discounts must be zero or more.');
      const validIds = this.selectedPeople.filter((id) => this.bill()!.people.some((p) => p.id === id));
      const weights = validIds.map((id) => ({ personId: id, share: Number(this.allocationWeights[id]) }));
      if (this.allocationMode === 'selected' && (!weights.length || weights.some((a) => !Number.isFinite(a.share) || a.share <= 0))) throw new Error('Choose at least one person and use positive shares.');
      const weightTotal = weights.reduce((s, a) => s + a.share, 0);
      const item: BillItem = {
        id: this.selectedItemId() ?? newId(), name: this.draft.name.trim(), quantity: this.draft.quantity,
        total: this.calculator.toMinor(this.draft.total!),
        unitPrice: this.draft.unitPrice == null ? undefined : this.calculator.toMinor(this.draft.unitPrice),
        unitDiscount: this.calculator.toMinor(this.draft.unitDiscount), discount: this.calculator.toMinor(this.draft.discount),
        allocations: this.allocationMode === 'everyone' ? [] : weights.map((a) => ({ ...a, share: a.share / weightTotal })),
        confidence: 1,
      };
      if (item.total >= 0 && this.calculator.calculateItemDiscount(item) > item.total) throw new Error('Item discounts cannot exceed the line price.');
      if (this.selectedItemId()) this.store.applyOperation(this.currentId(), { type: 'update_item', itemId: item.id, changes: item });
      else this.store.applyOperation(this.currentId(), { type: 'add_item', item });
      this.closeEditor();
      this.error.set('');
    } catch (e) { this.error.set(this.errorMessage(e)); }
  }
  removeItem(itemId: string): void {
    this.store.applyOperation(this.currentId(), { type: 'remove_item', itemId });
    this.closeEditor();
  }
  assign(itemId: string, personId?: string): void {
    if (!this.bill()?.items.some((i) => i.id === itemId)) return;
    this.store.applyOperation(this.currentId(), { type: 'assign_item', itemId, personIds: personId ? [personId] : [] });
    this.closeEditor();
    this.notice.set(personId ? 'Item moved to ' + this.bill()!.people.find((p) => p.id === personId)?.name + '.' : 'Item is now shared equally by everyone.');
  }
  allocationLabel(item: BillItem): string {
    const bill = this.bill()!;
    return this.calculator.isShared(item, bill.people) ? 'Shared by everyone' :
      bill.people.filter((p) => item.allocations.some((a) => a.personId === p.id && a.share > 0)).map((p) => p.name).join(' + ');
  }

  changeCharge(key: ChargeKey, field: 'value' | 'strategy' | 'personId' | 'mode', value: string | number): void {
    try {
      this.store.updateBill(this.currentId(), (bill) => {
        const charge = bill[key];
        if (field === 'value') {
          const number = Number(value);
          if (!Number.isFinite(number) || number < 0 || (charge.percentage != null && number > 100)) throw new Error('Enter a valid nonnegative adjustment (percentages up to 100).');
          if (charge.percentage != null) charge.percentage = number;
          else charge.amount = this.calculator.toMinor(number);
        } else if (field === 'mode') {
          charge.amount = 0; charge.percentage = value === 'percent' ? 0 : undefined;
        } else if (field === 'strategy') {
          charge.strategy = value as ChargeAllocationStrategy;
          charge.personId = value === 'specific-person' ? bill.people[0]?.id : undefined;
        } else charge.personId = String(value);
        return bill;
      });
      this.error.set('');
    } catch (e) { this.error.set(this.errorMessage(e)); }
  }
  changeCurrency(currency: string): void {
    this.store.updateBill(this.currentId(), (bill) => ({ ...bill, currency }));
  }
  async fileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.busy()) return;
    this.busy.set(true); this.status.set('Reading receipt…'); this.error.set('');
    try {
      const attachment = (await this.attachments.createAttachments([file]))[0];
      if (!attachment?.dataUrl && !attachment?.text?.trim()) throw new Error('This file has no readable content. For a scanned PDF, upload a receipt image instead.');
      await this.importSource('', attachment, '', true);
    } catch (e) { this.error.set(this.errorMessage(e)); }
    finally { this.busy.set(false); input.value = ''; }
  }
  async importText(): Promise<void> {
    if (this.pastedText.trim() && !this.busy()) await this.importSource(this.pastedText);
  }
  private async importSource(text: string, attachment?: ChatAttachment, instructions = '', prepared = false): Promise<void> {
    if (this.busy() && !prepared) return;
    const original = this.bill()!;
    this.busy.set(true); this.status.set('Extracting receipt items…'); this.error.set('');
    try {
      const extracted = await this.ai.extract(text, attachment, instructions, original.people);
      // Preserve edits made elsewhere while inference was running.
      if (this.store.getBill(original.id) !== original) throw new Error('The bill changed while reading the receipt. Please retry the import.');
      this.store.updateBill(original.id, () => ({
        ...extracted, id: original.id, people: original.people, sourceProcessed: true,
        items: [...original.items, ...extracted.items],
        // Adding another source must not wipe existing manually entered charges.
        ...(original.items.length ? { tax: original.tax, tip: original.tip, serviceCharge: original.serviceCharge, discount: original.discount, subtotal: undefined, statedTotal: undefined } : {}),
      }));
      this.lastSourceAttachment = attachment;
      this.importOpen.set(false);
      try {
        const saved = await this.storage.save(this.store.getBill(original.id)!, attachment);
        this.store.restoreBill(saved);
        this.notice.set(extracted.items.length + ' items added and saved. Review the receipt details before confirming.');
      } catch {
        this.notice.set(extracted.items.length + ' items added. Save the bill when the database is available.');
      }
    } catch (e) { this.error.set(this.errorMessage(e)); }
    finally { this.busy.set(false); this.status.set(''); }
  }
  confirm(): void {
    const bill = this.bill();
    if (!bill?.items.length || !bill.people.length || !this.validation()?.valid) return;
    this.confirmation.set(bill);
    this.completed.emit({ billId: bill.id, currency: bill.currency,
      people: bill.people.map((p) => ({ ...p, total: this.calculator.calculatePersonTotal(bill, p.id) })),
      total: this.calculator.calculateTotal(bill) });
  }
  drag(event: DragEvent, itemId: string): void {
    if (this.busy()) { event.preventDefault(); return; }
    event.dataTransfer?.setData('text/bill-item', itemId);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    this.dragging.set(true);
  }
  dragOver(event: DragEvent, target: string): void {
    if (!event.dataTransfer?.types.includes('text/bill-item') || this.busy()) return;
    event.preventDefault(); this.activeDrop.set(target);
    event.dataTransfer.dropEffect = 'move';
  }
  drop(event: DragEvent, personId?: string): void {
    event.preventDefault();
    const id = event.dataTransfer?.getData('text/bill-item');
    if (id && !this.busy()) this.assign(id, personId);
    this.dragEnd();
  }
  dragEnd(): void { this.dragging.set(false); this.activeDrop.set(undefined); }
  private errorMessage(e: unknown): string {
    if (e instanceof Error) return e.message;
    return 'The receipt could not be read. Retry or enter items manually.';
  }
  private emptyDraft() {
    return { name: '', quantity: 1, unitPrice: null as number | null, total: null as number | null, unitDiscount: 0, discount: 0 };
  }
}
